import {
    type CustomServiceDefinition,
    type OrderConfiguration,
    type OrderConfigurationParameter,
    type ServiceConfiguration,
} from "../api";
import { ValueObject } from "../base";
import { ServiceConfigurationEntity } from "./ServiceConfigurationEntity";
import { type ServiceDefinitionEntity } from "./ServiceDefinitionEntity";

export class OrderConfigurationValue
    extends ValueObject(
        class {
            readonly customServiceId: CustomServiceDefinition.Id;
            readonly name: string;
            readonly price: number | undefined;
            readonly parameters: OrderConfigurationParameter[];
            readonly serviceConfiguration: ServiceConfiguration;
            readonly remarks?: string;
            readonly attachments?: ServiceConfiguration.Attachment[];

            constructor(value: OrderConfiguration) {
                this.customServiceId = value.customServiceId;
                this.name = value.name;
                this.price = value.price;
                this.parameters = value.parameters;
                this.serviceConfiguration = value.serviceConfiguration;
                this.remarks = value.remarks;
                this.attachments = value.attachments;
            }

            public getCustomServiceId(): CustomServiceDefinition.Id {
                return this.customServiceId;
            }
            public getServiceName(): string {
                return this.name;
            }
            public getServicePrice(): number | undefined {
                return this.price;
            }
            public getParameters(): OrderConfigurationParameter[] {
                return this.parameters;
            }
            public getRemarks(): string | undefined {
                return this.remarks;
            }
            public getAttachments(): ServiceConfiguration.Attachment[] | undefined {
                return this.attachments;
            }
            public getServiceConfiguration(): ServiceConfiguration {
                return this.serviceConfiguration;
            }
        }
    )
    implements OrderConfiguration
{
    /**
     * Excludes the price from the order configuration.
     * This is useful for filtering price visibility based on user role and order stage.
     *
     * @returns A new OrderConfigurationValue instance with the price set to undefined
     */
    public excludePrice(): OrderConfigurationValue {
        return new OrderConfigurationValue({
            ...this,
            price: undefined,
        });
    }

    static create(
        orderConfiguration: OrderConfiguration | ServiceConfigurationEntity,
        serviceDefinition?: ServiceDefinitionEntity
    ): OrderConfigurationValue {
        if (orderConfiguration instanceof OrderConfigurationValue) {
            return orderConfiguration;
        }
        if (orderConfiguration instanceof ServiceConfigurationEntity) {
            if (serviceDefinition === undefined) {
                throw new Error(
                    `OrderConfigurationValue: Service definition is required when creating an order configuration from a service configuration.`
                );
            }
            const parameters: OrderConfigurationParameter[] = [];
            const remarksTexts: string[] = [];

            serviceDefinition?.parameterGroups
                .flatMap(group => group.parameters)
                .forEach(parameter => {
                    const parameterValue = parameter.getParameterValue();
                    const storedParam = orderConfiguration
                        .getParameters()
                        .find(it => it.id === parameter.getId());

                    // Handle remarks parameters - collect their values for the remarks section
                    if (parameterValue.getKind() === "remarks" && storedParam?.value) {
                        remarksTexts.push(storedParam.value as string);
                        return;
                    }

                    // Handle file parameters - show the filename(s) from attachments
                    if (parameterValue.getKind() === "file") {
                        const attachments = orderConfiguration.getAttachments();
                        if (attachments && attachments.length > 0) {
                            // For now, show all attachment filenames for this parameter
                            // In the future, we could match attachments to specific parameters
                            const fileNames = attachments.map(att => att.name);
                            if (fileNames.length > 0) {
                                parameters.push({
                                    name: storedParam?.name ?? parameter.getName(),
                                    value: fileNames.join(", "),
                                });
                            }
                        }
                        return;
                    }

                    // Handle all other parameters normally
                    // Prefer stored displayValue, fall back to dynamic calculation for backward compatibility
                    const viewText =
                        storedParam?.displayValue ??
                        parameterValue.getViewText(storedParam?.value ?? undefined);

                    if (viewText !== undefined) {
                        parameters.push({
                            // Prefer stored name (snapshot), fall back to current definition
                            name: storedParam?.name ?? parameter.getName(),
                            value: viewText,
                        });
                    }
                });

            // Combine all remarks with existing remarks
            const allRemarks = [orderConfiguration.getRemarks(), ...remarksTexts]
                .filter(text => text && text.trim().length > 0)
                .join("\n\n");

            return new OrderConfigurationValue({
                customServiceId: orderConfiguration.getCustomServiceId(),
                name: orderConfiguration.getServiceName(),
                price: orderConfiguration.getServicePrice().getSubTotal(),
                parameters: parameters,
                serviceConfiguration: orderConfiguration,
                remarks: allRemarks || undefined,
                attachments: orderConfiguration.getAttachments(),
            });
        }
        return new OrderConfigurationValue(orderConfiguration);
    }
}
